import { useEffect } from 'react'
import type { CSSProperties } from 'react'
import { ProductWordmark } from './Brand'
import { EditorialMedia } from './EditorialMedia'
import { SectionHeading } from './SectionHeading'
import { SmartLink } from './SmartLink'
import { usePreviewSelection } from './usePreviewSelection'
import { useLanguage } from '../i18n'

const productLogos: Record<string, string> = {
  NORTE: '/assets/brands/norte.webp',
  SUL: '/assets/brands/sul.webp',
  LESTE: '/assets/brands/leste.webp',
  OESTE: '/assets/brands/oeste.webp',
  GeniusLED: '/assets/brands/geniusled.webp',
  GeniusLIVE: '/assets/brands/geniuslive.webp',
  GeniusLINK: '/assets/brands/geniuslink.webp',
  GeniusLAN: '/assets/brands/geniuslan.webp',
}

export function Products() {
  const { copy } = useLanguage()
  const products = copy.products.items
  const selection = usePreviewSelection()
  const activeProduct = products[selection.activeIndex]
  const activeLogo = productLogos[activeProduct.name]

  useEffect(() => {
    const preloadedImages: HTMLImageElement[] = []
    const preload = window.setTimeout(() => {
      products.forEach((product) => {
        if (!product.media?.desktopSrc) return
        const image = new Image()
        image.src = product.media.desktopSrc
        preloadedImages.push(image)
      })
    }, 750)

    return () => {
      window.clearTimeout(preload)
      preloadedImages.length = 0
    }
  }, [products])

  useEffect(() => {
    const handleProductJump = (event: Event) => {
      const name = (event as CustomEvent<string>).detail
      const index = products.findIndex((product) => product.name === name)
      if (index >= 0) selection.commit(index)
    }

    window.addEventListener('geniuslead:select-product', handleProductJump)
    return () => window.removeEventListener('geniuslead:select-product', handleProductJump)
  }, [selection])

  return (
    <section id="produtos" className="section products" aria-labelledby="products-title">
      <div className="container">
        <SectionHeading id="products-title" kicker={copy.products.kicker} title={copy.products.title} mobileTitle={copy.products.mobileTitle} />

        <div className="products-layout">
          <article className="product-stage" style={{ '--product-color': activeProduct.color } as CSSProperties} aria-live="polite">
            <EditorialMedia
              {...activeProduct.media}
              label={copy.products.mediaLabel}
              alt={activeProduct.media?.alt ?? ''}
              index={selection.activeIndex}
              className="product-stage__media"
            />
            <div className="product-stage__identity">
              {activeLogo ? <img src={activeLogo} alt={activeProduct.name} className="product-stage__logo" /> : <ProductWordmark name={activeProduct.name} />}
              {activeProduct.tagline && <p>{activeProduct.tagline}</p>}
              {activeProduct.highlight && <p className="product-stage__highlight">{activeProduct.highlight}</p>}
              <SmartLink
                className="text-link product-stage__cta"
                href={activeProduct.url}
              >
                {activeProduct.cta}
              </SmartLink>
            </div>
          </article>

          <div className="product-content">
            <div ref={selection.scopeRef} className="product-selectors">
              {products.map((product, index) => (
                <button
                  key={product.name}
                  type="button"
                  aria-label={`Selecionar ${product.name}`}
                  className={selection.activeIndex === index ? 'active' : ''}
                  aria-pressed={selection.selectedIndex === index}
                  onMouseEnter={() => selection.preview(index)}
                  onFocus={() => selection.preview(index)}
                  onClick={() => selection.commit(index)}
                >
                  <span className="product-selector__identity">
                    {productLogos[product.name] && <img src={productLogos[product.name]} alt="" aria-hidden="true" />}
                    <ProductWordmark name={product.name} />
                  </span>
                </button>
              ))}
            </div>

            <div className="product-details" key={activeProduct.name}>
              <dl>
                <div><dt>{copy.products.problem}</dt><dd>{activeProduct.problem}</dd></div>
                <div><dt>{copy.products.solution}</dt><dd>{activeProduct.solution}</dd></div>
                <div><dt>{copy.products.gain}</dt><dd>{activeProduct.gain}</dd></div>
              </dl>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
